import { SelectionModel } from "@angular/cdk/collections";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTable, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { TIMESHEET_ITEM_TYPES } from "src/app/items/static-forms/timesheet-item-types";
import { Preset } from "src/app/settings/models/preset";
import {
  TimeManagementSettings,
  TimesheetActivity as PresetActivity,
} from "src/app/settings/models/timesheet-settings";
import { PresetsService } from "src/app/settings/services/presets.service";
import { TimeManagementService } from "src/app/settings/services/time-management-service";

import {
  TimesheetActivity,
  TimesheetActivityError,
  TimesheetActivityStatus,
} from "src/app/inspection/model/timesheet-activity";
import { TimesheetActivitiesService } from "src/app/inspection/services/timesheet-activities.service";
import { InspectionDetailsService } from "src/app/inspection/services/inspection-details.service";
import { InspectionTimeManagementState } from "src/app/inspection/constants/constants";

@Component({
  selector: "im-inspection-timelog-timesheet",
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: "./inspection-timelog-timesheet.component.html",
  styleUrls: ["./inspection-timelog-timesheet.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionTimesheetComponent implements OnInit {
  public TimesheetActivityStatus = TimesheetActivityStatus;
  private readonly timesheetActivitiesService = inject(TimesheetActivitiesService);
  private readonly presetsService = inject(PresetsService);
  private readonly timeManagementService = inject(TimeManagementService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly inspectionDetailService = inject(InspectionDetailsService);

  public TimesheetActivityError = TimesheetActivityError;
  public InspectionTimeManagementState = InspectionTimeManagementState;

  public isLoading = signal<boolean>(false);
  public areNoActivities = signal<boolean>(true);
  public arePendingActivities = signal<boolean>(false);
  public areRejectedActivities = signal<boolean>(false);
  public allActivitiesApproved = signal<boolean>(false);
  public allActivitiesSubmitted = signal<boolean>(false);
  public isSubmitting = signal<boolean>(false);
  public submitButtonDisabled = signal<boolean>(true);
  public inspectionTimeSheetState = signal<InspectionTimeManagementState>(InspectionTimeManagementState.Pending);
  
  private inspectionId: string = "";

  @ViewChild("matTable", { read: ElementRef })
  public matTableElement!: ElementRef<HTMLElement>;
  @ViewChild(MatTable) public table!: MatTable<any>;

  public displayedColumns = signal<string[]>([
    "checkbox",
    "date",
    "activity",
    "itemType",
    "numberOfItems",
    "dayShiftHours",
    "nightShiftHours",
    "overtimeHours",
    "doubleShiftHours",
    "status",
    "remarks",
    "actions",
  ]);

  public previousDatasource = signal<TimesheetActivity[]>([]);
  public datasource = signal<TimesheetActivity[]>([]);
  public selection = new SelectionModel<TimesheetActivity>(true, []);

  private readonly inspectionPreset = signal<Preset>(null as any);
  public activities = signal<PresetActivity[]>([]);
  public itemTypes = signal<{ id: number; name: string }[]>(TIMESHEET_ITEM_TYPES);
  
  public async ngOnInit(): Promise<void> {
    this.getInspectionIdFromUrl();
    this.isLoading.set(true);
    this.inspectionTimeSheetState.set(await this.timeManagementService.getInspectionTimeManagementState(this.inspectionId));
    await this.getTimeManagementSettings();    
    await this.getTimesheetActivities();    
    
    this.inspectionDetailService.inspectionCommonData.subscribe(async data => {
      const externalId = data?.name;
      await this.getActivitiesCatalog(externalId);
    });
    this.isLoading.set(false);
  }

  private getInspectionIdFromUrl(): void {
    this.activatedRoute.parent?.params.subscribe((params) => {
      this.inspectionId = params["id"];
    });
  }

  public filterFutureDates = (date: Date): boolean => {
    return date < new Date();
  };

  public isRowLocked(row: TimesheetActivity): boolean {
    return row.status === TimesheetActivityStatus.Approved ||
           row.status === TimesheetActivityStatus.Submitted;;
  }

  public actionButtonDisabled(row: TimesheetActivity): boolean {
    return row.status === TimesheetActivityStatus.Rejected || 
           row.status === TimesheetActivityStatus.Approved ||
           row.status === TimesheetActivityStatus.Submitted;
  }

  public addNewActivityButtonDisabled(): boolean {
    return this.allActivitiesApproved() || this.allActivitiesSubmitted();
  }

  private async getTimeManagementSettings(): Promise<void> {
    const timeManagementSettings: TimeManagementSettings =
      await this.timeManagementService.getTimeManagementSettings();
    if (timeManagementSettings.overtimeCalculation != null) {
      this.displayedColumns.update((columns) =>
        columns.filter((column) => column !== "overtimeHours"),
      );
    }
  }

  private async getActivitiesCatalog(inspectionExternalId: string): Promise<void> {
    const inspectionPresetId = await this.presetsService.getPresetIdFromExternalId(inspectionExternalId);
    const allPresets = await this.presetsService.getPresetsDetails();
    const inspectionPreset = allPresets.find((preset) => preset.id === inspectionPresetId);
    if (!inspectionPreset) {
      return
    }

    this.inspectionPreset.set(inspectionPreset);

    let allActivities: PresetActivity[] = [];
    const preset = this.inspectionPreset();
    if (preset?.timesheetActivities) {
      allActivities = allActivities.concat(
        preset.timesheetActivities.map((activity) => {
          return { ...activity, name: preset.name + " - " + activity.name };
        }),
      );
    }

    this.activities.set(allActivities);
  }

  private async getTimesheetActivities(): Promise<void> {
    this.isLoading.set(true);
    const activities = await this.timesheetActivitiesService.getInspectorTimesheetActivities(this.inspectionId);
    this.datasource.set(activities);
    this.previousDatasource.set(activities.map((row) => ({ ...row })));

    this.changeBannerStatus(activities);

    this.isLoading.set(false);
  }

  private changeBannerStatus(activities: TimesheetActivity[]): void {
    this.arePendingActivities.set(activities.some((activity) => activity.status === TimesheetActivityStatus.Pending));
    this.areRejectedActivities.set(activities.some((activity) => activity.status === TimesheetActivityStatus.Rejected));
    this.areNoActivities.set(activities.length === 0);
    this.allActivitiesApproved.set(activities.length > 0 && activities.every((activity) => activity.status === TimesheetActivityStatus.Approved));    
    this.allActivitiesSubmitted.set(
      activities.some((activity) => activity.status === TimesheetActivityStatus.Submitted) &&
      !activities.some((activity) => activity.status === TimesheetActivityStatus.Pending) &&
      !activities.some((activity) => activity.status === TimesheetActivityStatus.Rejected)
    );
  }

  public async submit(): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "send",
        okButton: {
          text: "Confirm",
          color: "primary",
        },
        cancelButton: {
          text: "Cancel",
        },
        title: "Submit Timesheet",
        message: "Timesheet is about to be submitted for review and approval. Would you like to proceed?",
      },
      autoFocus: false,
    });

    const confirmDialogResult = await firstValueFrom(confirmDialog.afterClosed());
    if (!confirmDialogResult) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      await this.timesheetActivitiesService.submitTimesheetActivities(this.inspectionId);
    } finally {
      this.isSubmitting.set(false);
    }
    this.datasource.update((activities) =>
      activities
        .map((activity) =>
          activity.status === TimesheetActivityStatus.Pending || activity.status === TimesheetActivityStatus.Rejected
            ? { ...activity, status: TimesheetActivityStatus.Submitted }
            : activity
        )
        .filter((activity) => activity.isValid !== false)
    );
    this.previousDatasource.set(this.datasource().map((r) => ({ ...r })));
    this.changeBannerStatus(this.datasource());
    this.submitButtonDisabled.set(false);
    this.cdr.detectChanges();
  }

  public addNewTimesheetActivity(): void {
    const newTimesheetActivity: TimesheetActivity = {
      id: "",
      date: "",
      externalId: this.inspectionId,
      userEmail: "",
      activityId: "",
      activityName: "",
      itemType: this.itemTypes().find((item) => item.name === "Item")?.id ?? 3,
      numberOfItems: 0,
      dayShiftHours: "00H 00m",
      overtimeHours: "00H 00m",
      nightShiftHours: "00H 00m",
      doubleShiftHours: "00H 00m",
      status: TimesheetActivityStatus.Pending,
      remarks: "",
      inspectionContractLocalNumber: "",
      isValid: false,
      validityError: "",
    };
    this.addNewRowToDatasource(newTimesheetActivity);
  }

  public cloneTimesheetActivity(timesheetActivity: TimesheetActivity): void {
    const clonedTimesheetActivity: TimesheetActivity = {
      ...timesheetActivity,
      id: "",
      numberOfItems: 0,
      dayShiftHours: "00H 00m",
      overtimeHours: "00H 00m",
      nightShiftHours: "00H 00m",
      doubleShiftHours: "00H 00m",
      activityId: "",
      activityName: "",
      status: TimesheetActivityStatus.Pending,
      remarks: "",
      isValid: false,
      validityError: "",
    };
    this.addNewRowToDatasource(clonedTimesheetActivity);
  }

  private addNewRowToDatasource(row: TimesheetActivity): void {
    this.datasource.update((activities) => [{ ...row }, ...activities]);
    this.previousDatasource.update((activities) => [{ ...row }, ...activities]);
    setTimeout(() => {
      this.matTableElement.nativeElement.scrollTop = 0;
    }, 0);
  }

  public isAllSelected(): boolean {
    return this.selection.selected.length === this.datasource().length;
  }

  public changeAllSelectedRows(row: any, property: string): void {
    if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
      for (const selectedRow of this.selection.selected) {
        if (
          property === "status" ||
          selectedRow.status !== TimesheetActivityStatus.Approved
        ) {
          (selectedRow as any)[property] = row[property];

          if (property === "activityId") {
            this.matchActivityIdWithActivityName(row);
          }
        }
      }
    }
  }

  public matchActivityIdWithActivityName(row: any): void {
    const activity = this.activities().find(
      (activity) => activity.id === row.activityId,
    );
    if (activity) {
      row.activityName = activity.name.split("-")[1]?.trim() ?? activity.name;
    }
  }

  public onlyNumbers(event: KeyboardEvent): void {
    const pattern = /\d/;
    if (!pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  public preventPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public formatTimeString(row: any, property: string): void {    
    const stringInput = row[property].replaceAll(/\D/g, "");
    const stringInputClean = stringInput.replace(/^0+/, "");    
    const numericInput: number = +stringInputClean;

    if (
      numericInput > 2359 ||
      (numericInput.toString().length === 2 &&
        stringInput.length === 2 &&
        numericInput > 23)
    ) {
      row[property] = "23H 59m";
    } else {
      switch (stringInput.length) {
        case 0:
          row[property] = "00H 00m";
          break;
        case 1:
          row[property] = `0${stringInput}H 00m`;
          break;
        case 2:
          row[property] = `${stringInput}H 00m`;
          break;
        case 3:
          if (+stringInput.slice(1, 3) > 59) {
            row[property] = `0${stringInput.slice(0, 1)}H 59m`;
          } else {
            row[property] = `0${stringInput.slice(0, 1)}H ${stringInput.slice(1, 3)}m`;
          }
          break;
        case 4:
          if (+stringInput.slice(2, 4) > 59) {
            row[property] = `${stringInput.slice(0, 2)}H 59m`;
          } else {
            row[property] = `${stringInput.slice(0, 2)}H ${stringInput.slice(2, 4)}m`;
          }
          break;
        default:
          row[property] = "23H 59m";
          break;
      }
    }
  }

  public async checkValidityAndSave(index: number, property: string): Promise<void> {
    const row = this.datasource()[index];
    const previousRow = this.previousDatasource()[index];

    if ((row as any)[property] !== (previousRow as any)[property]) {
      if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
        for (const selectedRow of this.selection.selected) {
          const rowIndex = this.datasource().findIndex((r) => r.id === selectedRow.id);
          await this.handleSingleRowValidityAndSave(selectedRow, rowIndex);
        }
      } else {
        await this.handleSingleRowValidityAndSave(row, index);
      }

      this.previousDatasource.set(this.datasource().map((r) => ({ ...r })));
    }
  }

  private async handleSingleRowValidityAndSave(
    element: TimesheetActivity,
    index: number,
  ): Promise<void> {
    const isRowValid = this.validateRow(element);
    if (isRowValid) {
      this.datasource()[index].isValid = true;
      this.datasource()[index].validityError = "";
      this.cdr.detectChanges();
      if (this.datasource()[index].id) {
        try {
          await this.timesheetActivitiesService.updateTimesheetActivityFromInspectorRole(
            this.datasource()[index],
            this.inspectionId,
          );          
        } catch (error) {
          this.datasource()[index].isValid = false;
          if (error === TimesheetActivityError.InvalidDateDueToTimeOff) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidDateDueToTimeOff;
          } else if (error === TimesheetActivityError.InvalidHoursMoreThanAllowed) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidHoursMoreThanAllowed;
          } else if (error === TimesheetActivityError.RemarkIsMandatory) {
            this.datasource()[index].validityError =
              TimesheetActivityError.RemarkIsMandatory;
          }
          this.cdr.detectChanges();
        }
      } else {
        try {
          const newActivityId =
            await this.timesheetActivitiesService.saveTimesheetActivityFromInspectorRole(
              this.datasource()[index],
              this.inspectionId,
            );
          this.datasource()[index].id = newActivityId;
          this.submitButtonDisabled.set(false);
        } catch (error) {
          this.datasource()[index].isValid = false;
          if (error === TimesheetActivityError.InvalidDateDueToTimeOff) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidDateDueToTimeOff;
          } else if (error === TimesheetActivityError.InvalidHoursMoreThanAllowed) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidHoursMoreThanAllowed;
          } else if (error === TimesheetActivityError.RemarkIsMandatory ) {
            this.datasource()[index].validityError =
              TimesheetActivityError.RemarkIsMandatory;
          }
          this.cdr.detectChanges();
        }
      }
    } else {
      this.datasource()[index].isValid = false;
      this.cdr.detectChanges();
    }
  }

  private validateRow(row: TimesheetActivity): boolean {
    if (!row.date) return false;
    if (!row.activityId) return false;
    if (!row.activityName) return false;
    return true;
  }

  public async openDeleteConfirmDialog(row: TimesheetActivity): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "delete_outline",
        okButton: {
          text: "Yes",
          color: "warn",
        },
        cancelButton: {
          text: "No",
        },
        title: "Delete Timesheet Activity",
        message: "Are you sure you want to delete this entry?",
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });

    const confirmDialogResult = await firstValueFrom(confirmDialog.afterClosed());
    if (confirmDialogResult) {
      if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
        let hasToShowInfoBanner = false;
        for (const selectedRow of this.selection.selected) {
          if (
            selectedRow.status !== TimesheetActivityStatus.Approved &&
            selectedRow.id
          ) {
            await this.deleteTimesheetActivityRow(selectedRow);
          } else {
            hasToShowInfoBanner = true;
          }
        }

        if (hasToShowInfoBanner) {
          this.snackbar.open(
            "Approved and newly created activities cannot be deleted. Please, change the status in order to do so.",
            "CLOSE",
            {
              duration: 3000,
              panelClass: "info-snackbar",
            },
          );
        }
      } else {
        await this.deleteTimesheetActivityRow(row);
        
      }
    }
  }

  private async deleteTimesheetActivityRow(row: TimesheetActivity): Promise<void> {
    try {
      await this.timesheetActivitiesService.deleteTimesheetActivity(row.id);
    } finally {
      this.removeRowFromSelection(row);
      this.removeRowFromDatasources(row);
      this.changeBannerStatus(this.datasource());
      this.submitButtonDisabled.set(!this.arePendingActivities() && !this.areRejectedActivities());
      this.cdr.detectChanges();
    }
  }

  private removeRowFromSelection(row: TimesheetActivity): void {
    if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
      this.selection.deselect(row);
    }
  }

  private removeRowFromDatasources(row: TimesheetActivity): void {
    this.datasource.update((activities) =>
      activities.filter((a) => a.id !== row.id),
    );
    this.previousDatasource.update((activities) =>
      activities.filter((a) => a.id !== row.id),
    );
  }

  public greaterThanAllowedShift(shift: string): boolean {
    const splittedShift = shift.split("H");
    if (+splittedShift[0].trim() > 12) return true;
    if (
      +splittedShift[0].trim() === 12 &&
      +splittedShift[1].trim().slice(0, 2) > 0
    ) {
      return true;
    }
    return false;
  }

  public getStatusLabel(status: TimesheetActivityStatus): string {
    switch (status) {
      case TimesheetActivityStatus.Pending:
        return "Pending";
      case TimesheetActivityStatus.Approved:
        return "Approved";
      case TimesheetActivityStatus.Rejected:
        return "Rejected";
      case TimesheetActivityStatus.Submitted:
        return "Submitted";
      default:
        return "";
    }
  }
}
